import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  Row,
  Col,
  Label,
  FormGroup,
} from 'reactstrap';
import { PageTitle, Button } from '@cordata/buffet';
import Select from 'react-select';
import querystring from 'querystring';

import UncontrolledTable from '~/components/common/UncontrolledTable';
import useSlice from '~/utils/slice/hook';
import { adminInstance } from '~/utils';

const UserListExport = () => {
  const { fetch } = useSlice();

  // State for filter options
  const [markets, setMarkets] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [subSpecialties, setSubSpecialties] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // State for selected filters
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedSubSpecialty, setSelectedSubSpecialty] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);

  // State for row count
  const [totalRows, setTotalRows] = useState(0);

  // Build endpoint params based on filters
  const buildEndpointParams = useCallback(() => {
    const params = {};
    if (selectedMarket) params.market_uid = selectedMarket.id;
    if (selectedSpecialty) params.specialty_uid = selectedSpecialty.id;
    if (selectedSubSpecialty) params.sub_specialty_uid = selectedSubSpecialty.id;
    if (selectedOrg) params.org_uid = selectedOrg.id;
    if (selectedDivision) params.division_uid = selectedDivision.id;
    return params;
  }, [selectedMarket, selectedSpecialty, selectedSubSpecialty, selectedOrg, selectedDivision]);

  // Fetch total count when filters change
  useEffect(() => {
    const params = buildEndpointParams();
    adminInstance
      .get('/user-list-export', { params })
      .then(response => {
        if (response.data && response.data.total !== undefined) {
          setTotalRows(response.data.total);
        }
      })
      .catch(err => console.error('Error fetching total:', err));
  }, [buildEndpointParams]);

  // Load filter options on mount
  useEffect(() => {
    // Load markets
    fetch('market', { 'all-data': '1' })
      .then(r => r.data.data)
      .then(setMarkets);

    // Load organizations
    fetch('organization', { 'all-data': '1' })
      .then(r => r.data.data)
      .then(setOrganizations);
  }, []);

  // Load specialties when market changes
  useEffect(() => {
    if (selectedMarket) {
      fetch('specialty', {
        'market-uid': selectedMarket.id,
        'all-data': '1',
      })
        .then(r => r.data.data)
        .then(setSpecialties);
    } else {
      setSpecialties([]);
      setSelectedSpecialty(null);
    }
  }, [selectedMarket]);

  // Load subspecialties when specialty changes
  useEffect(() => {
    if (selectedSpecialty) {
      fetch('sub-specialty', {
        'specialty-uid': selectedSpecialty.id,
        'all-data': '1',
      })
        .then(r => r.data.data)
        .then(setSubSpecialties);
    } else {
      setSubSpecialties([]);
      setSelectedSubSpecialty(null);
    }
  }, [selectedSpecialty]);

  // Load divisions when org changes
  useEffect(() => {
    if (selectedOrg) {
      fetch('division', {
        'org-uid': selectedOrg.id,
        'all-data': '1',
      })
        .then(r => r.data.data)
        .then(setDivisions);
    } else {
      setDivisions([]);
      setSelectedDivision(null);
    }
  }, [selectedOrg]);

  // Memoize the endpoint to prevent unnecessary re-fetches
  const tableEndpoint = useMemo(() => {
    const params = buildEndpointParams();
    const endpoint = {
      url: 'admin/user-list-export',
      // Only include params if there are any - matches pattern used by other tables
      ...(Object.keys(params).length > 0 && { params }),
    };
    console.log('[UserListExport] Endpoint:', endpoint);
    return endpoint;
  }, [buildEndpointParams]);

  // Handle CSV export
  const handleExportCSV = async () => {
    const params = buildEndpointParams();

    // Get current sort state from localStorage (saved by UncontrolledTable)
    const sortedState = localStorage.getItem('user-list-export-table-state-sorted');
    if (sortedState) {
      try {
        const sorted = JSON.parse(sortedState);
        // Convert to API format: [{ id: 'last_name', desc: false }] => 'last_name' or '-last_name'
        if (sorted && sorted.length > 0) {
          params.sort = sorted
            .map(s => `${s.desc ? '-' : ''}${s.id}`)
            .join(',');
        }
      } catch (e) {
        console.error('Error parsing sort state:', e);
      }
    }

    // Get current filtered state from localStorage (saved by UncontrolledTable)
    const filteredState = localStorage.getItem('user-list-export-table-state-filtered');
    if (filteredState) {
      try {
        const filtered = JSON.parse(filteredState);
        // Add each filter to params: [{ id: 'last_name', value: 'bro' }] => { last_name: 'bro' }
        if (filtered && filtered.length > 0) {
          filtered.forEach(filter => {
            if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
              params[filter.id] = filter.value;
            }
          });
        }
      } catch (e) {
        console.error('Error parsing filtered state:', e);
      }
    }

    console.log('[UserListExport] CSV export params:', params);

    try {
      // Use adminInstance to fetch CSV with authentication
      // Use querystring serializer to properly handle params (same as UncontrolledTable)
      const response = await adminInstance.get('/user-list-export/csv', {
        params,
        responseType: 'blob',
        paramsSerializer: p => querystring.stringify(p),
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_list.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  // Table columns
  const columns = [
    {
      Header: 'Display Name',
      accessor: 'attributes.displayName',
      id: 'display_name',
    },
    {
      Header: 'First Name',
      accessor: 'attributes.firstName',
      id: 'first_name',
    },
    {
      Header: 'Last Name',
      accessor: 'attributes.lastName',
      id: 'last_name',
    },
    {
      Header: 'Email',
      accessor: 'attributes.emailAddress',
      id: 'email_address',
    },
    {
      Header: 'Organization',
      accessor: 'attributes.orgName',
      id: 'org_name',
    },
    {
      Header: 'Divisions',
      accessor: 'attributes.divisionNames',
      id: 'division_names',
      Cell: ({ value }) => (value && value.length > 0 ? value.join(', ') : ''),
    },
    {
      Header: 'Auth Service',
      accessor: 'attributes.authenticationService',
      id: 'authentication_service',
    },
    {
      Header: '2FA Enabled',
      accessor: 'attributes.mfaRequired',
      id: 'mfa_required',
      Cell: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      Header: '2FA Method',
      accessor: 'attributes.mfaType',
      id: 'mfa_type',
    },
    {
      Header: 'Last Login',
      accessor: 'attributes.lastLoginDate',
      id: 'last_login_date',
    },
    {
      Header: 'Created Date',
      accessor: 'attributes.createdDate',
      id: 'created_date',
    },
  ];

  return (
    <>
      <PageTitle>User List for Communication</PageTitle>

      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Market</Label>
                <Select
                  value={selectedMarket}
                  onChange={setSelectedMarket}
                  options={markets}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.marketLabel}
                  isClearable
                  placeholder="Select market..."
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Specialty</Label>
                <Select
                  value={selectedSpecialty}
                  onChange={setSelectedSpecialty}
                  options={specialties}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.specialtyLabel}
                  isClearable
                  isDisabled={!selectedMarket}
                  placeholder="Select specialty..."
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Subspecialty</Label>
                <Select
                  value={selectedSubSpecialty}
                  onChange={setSelectedSubSpecialty}
                  options={subSpecialties}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.subSpecialtyLabel}
                  isClearable
                  isDisabled={!selectedSpecialty}
                  placeholder="Select subspecialty..."
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Organization</Label>
                <Select
                  value={selectedOrg}
                  onChange={setSelectedOrg}
                  options={organizations}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.orgName}
                  isClearable
                  placeholder="Select organization..."
                />
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Division</Label>
                <Select
                  value={selectedDivision}
                  onChange={setSelectedDivision}
                  options={divisions}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.divisionName}
                  isClearable
                  isDisabled={!selectedOrg}
                  placeholder="Select division..."
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button color="primary" onClick={handleExportCSV}>
              Export to CSV
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Card>
        <CardBody>
          <div className="mb-3">
            <strong>Total Users: {totalRows.toLocaleString()}</strong>
          </div>
          <UncontrolledTable
            name="user-list-export"
            endpoint={tableEndpoint}
            columns={columns}
            defaultPageSize={20}
            defaultSorted={[{ id: 'last_name', desc: false }]}
          />
        </CardBody>
      </Card>
    </>
  );
};

export default UserListExport;
